import React, { useEffect, useState, useCallback } from 'react';
import { Box, Typography, Paper, TextField, Button, Stack, Collapse, IconButton, LinearProgress, Alert, Snackbar, Chip, Switch, FormControlLabel, Accordion, AccordionSummary, AccordionDetails, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import BuildIcon from '@mui/icons-material/Build';
import StorageIcon from '@mui/icons-material/Storage';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ScheduleIcon from '@mui/icons-material/Schedule';
import DuplicateClusterCard from './DuplicateClusterCard';
import axiosInstance from '../../services/axiosInstance';

interface Props {
  onClose: () => void;
}

const DuplicateListPage: React.FC<Props> = ({ onClose }) => {
  const [assetId, setAssetId] = useState('');
  const [exact, setExact] = useState<any[]>([]);
  const [near, setNear] = useState<any[]>([]);
  const [clusters, setClusters] = useState<any[]>([]);
  const [threshold, setThreshold] = useState(8);
  const [error, setError] = useState<string | null>(null);
  const [showTools, setShowTools] = useState(false);
  const [loading, setLoading] = useState(false);
  const [indexingInProgress, setIndexingInProgress] = useState(false);
  const [rebuildingInProgress, setRebuildingInProgress] = useState(false);
  const [snack, setSnack] = useState<{open: boolean; message: string; severity: 'success'|'info'|'warning'|'error'}>({open: false, message: '', severity: 'info'});

  // Schedule state
  const [scheduleSettings, setScheduleSettings] = useState<{enabled: boolean; dailyTime: string; retentionDays: number}>({ enabled: false, dailyTime: '03:00', retentionDays: 30 });
  const [scheduleLoading, setScheduleLoading] = useState(true);
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [scheduleMsg, setScheduleMsg] = useState<{type: 'success'|'error'; text: string} | null>(null);
  const [scheduleLogs, setScheduleLogs] = useState<any[]>([]);
  const [scheduleLogsLoading, setScheduleLogsLoading] = useState(false);
  const [selectedScheduleLog, setSelectedScheduleLog] = useState<any>(null);

  const loadScheduleSettings = useCallback(async () => {
    try {
      setScheduleLoading(true);
      const { data } = await axiosInstance.get('/api/duplicates/schedule/settings');
      if (data.settings) setScheduleSettings(data.settings);
    } catch (err: any) {
      console.error('Error loading duplicate schedule settings:', err);
    } finally {
      setScheduleLoading(false);
    }
  }, []);

  const loadScheduleLogs = useCallback(async () => {
    try {
      setScheduleLogsLoading(true);
      const { data } = await axiosInstance.get('/api/duplicates/schedule/logs');
      if (data.logs) setScheduleLogs(data.logs);
    } catch (err: any) {
      console.error('Error loading duplicate schedule logs:', err);
    } finally {
      setScheduleLogsLoading(false);
    }
  }, []);

  const handleSaveSchedule = async () => {
    try {
      setScheduleSaving(true);
      setScheduleMsg(null);
      const { data } = await axiosInstance.put('/api/duplicates/schedule/settings', scheduleSettings);
      if (data.settings) setScheduleSettings(data.settings);
      setScheduleMsg({ type: 'success', text: 'Inställningar sparade' });
    } catch (err: any) {
      setScheduleMsg({ type: 'error', text: err.response?.data?.message || 'Kunde inte spara' });
    } finally {
      setScheduleSaving(false);
    }
  };

  const handleViewScheduleLog = async (dateKey: string) => {
    try {
      setSelectedScheduleLog(null);
      const { data } = await axiosInstance.get(`/api/duplicates/schedule/logs/${dateKey}`);
      if (data.log) setSelectedScheduleLog(data.log);
    } catch (err: any) {
      console.error('Error loading schedule log:', err);
    }
  };

  const runSearch = async () => {
    try {
      setError(null);
      setExact([]); setNear([]);
      if (!assetId) return;
      const [ex, nr] = await Promise.all([
        axiosInstance.get(`/api/duplicates/exact/${assetId}`),
        axiosInstance.get(`/api/duplicates/near/${assetId}`, { params: { threshold } })
      ]);
      setExact(ex.data.items || []);
      setNear(nr.data.items || []);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Fel vid dublettsökning.');
    }
  };

  const loadClusters = async (rebuild = false) => {
    try {
      setLoading(true);
      setError(null);
      if (rebuild) {
        await axiosInstance.post('/api/duplicates/clusters/rebuild');
      }
      const resp = await axiosInstance.get('/api/duplicates/clusters', { params: { limit: 50 } });
      const exactClusters = resp.data.items?.filter((c: any) => c.type === 'exact') || [];
      setClusters(exactClusters);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Kunde inte ladda dubletter');
    } finally {
      setLoading(false);
    }
  };

  const handleIndexAll = async () => {
    if (!window.confirm('Detta beräknar hash-värden för ALLA bilder i systemet.\n\nProcessen körs i bakgrunden och kan ta flera minuter beroende på antal bilder.\n\nFortsätt?')) {
      return;
    }
    
    try {
      setIndexingInProgress(true);
      setSnack({ open: true, severity: 'info', message: 'Startar hash-beräkning för alla bilder...' });
      await axiosInstance.post('/api/duplicates/index-all');
      setSnack({ open: true, severity: 'success', message: 'Hash-beräkning startad! Processen körs i bakgrunden. Klicka på "Uppdatera dublett-lista" om en stund för att se resultatet.' });
      // After starting indexing, rebuild clusters after a short delay
      setTimeout(async () => {
        try {
          await axiosInstance.post('/api/duplicates/clusters/rebuild');
          await loadClusters(false);
        } catch {}
        setIndexingInProgress(false);
      }, 3000);
    } catch (e: any) {
      setSnack({ open: true, severity: 'error', message: e?.response?.data?.message || 'Kunde inte starta indexering' });
      setIndexingInProgress(false);
    }
  };

  const handleRebuildClusters = async () => {
    try {
      setRebuildingInProgress(true);
      setSnack({ open: true, severity: 'info', message: 'Uppdaterar dublett-listan...' });
      await axiosInstance.post('/api/duplicates/clusters/rebuild');
      await loadClusters(false);
      setSnack({ open: true, severity: 'success', message: 'Dublett-listan har uppdaterats!' });
    } catch (e: any) {
      setSnack({ open: true, severity: 'error', message: e?.response?.data?.message || 'Kunde inte uppdatera listan' });
    } finally {
      setRebuildingInProgress(false);
    }
  };

  useEffect(() => {
    loadClusters(false);
    loadScheduleSettings();
    loadScheduleLogs();
  }, [loadScheduleSettings, loadScheduleLogs]);

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', position: 'relative', p: 2 }}>
      <IconButton aria-label="Stäng" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8, zIndex: 1 }}>
        <CloseIcon />
      </IconButton>
      <Typography variant="h5" sx={{ mb: 1, pr: 5 }}>Dublettkontroll</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Systemet visar bilder som är exakt identiska (samma fil). Välj två bilder (A och B) för att jämföra, slå ihop eller markera som ej dublett.
      </Typography>
      
      {/* Progress indicator for background operations */}
      {(indexingInProgress || rebuildingInProgress) && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress color={indexingInProgress ? 'warning' : 'primary'} />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {indexingInProgress ? 'Beräknar hash-värden i bakgrunden...' : 'Uppdaterar dublett-listan...'}
          </Typography>
        </Box>
      )}
      
      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Button 
          variant="contained" 
          onClick={() => loadClusters(false)}
          disabled={loading || rebuildingInProgress}
          startIcon={<RefreshIcon />}
        >
          {loading ? 'Uppdaterar...' : 'Uppdatera lista'}
        </Button>
        <Button 
          variant="outlined" 
          onClick={() => setShowTools(v => !v)}
          startIcon={<BuildIcon />}
        >
          {showTools ? 'Dölj avancerat' : 'Avancerade verktyg'}
        </Button>
      </Stack>

      <Collapse in={showTools}>
        <Paper 
          sx={{ 
            p: 2, 
            mb: 2, 
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            boxShadow: 'none'
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
            Avancerade verktyg (endast för admins)
          </Typography>
          
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Hur dublettkontroll fungerar:</strong><br/>
              1. Systemet beräknar ett unikt "fingeravtryck" (hash) för varje bild<br/>
              2. Bilder med samma fingeravtryck är exakta kopior<br/>
              3. Om du laddat upp nya bilder, klicka först på <strong>"Beräkna hash för nya bilder"</strong>, sedan <strong>"Uppdatera dublett-lista"</strong>
            </Typography>
          </Alert>

          <Stack spacing={3}>
            {/* System-underhåll - Primära knappar */}
            <Box>
              <Typography variant="body2" fontWeight="bold" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <StorageIcon fontSize="small" /> System-underhåll
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <Button 
                  variant="outlined" 
                  color="primary"
                  onClick={handleRebuildClusters}
                  disabled={rebuildingInProgress || indexingInProgress}
                  fullWidth
                  startIcon={<RefreshIcon />}
                >
                  {rebuildingInProgress ? 'Uppdaterar...' : 'Uppdatera dublett-lista'}
                </Button>
                <Button 
                  variant="outlined" 
                  color="warning" 
                  onClick={handleIndexAll}
                  disabled={indexingInProgress || rebuildingInProgress}
                  fullWidth
                  startIcon={<StorageIcon />}
                >
                  {indexingInProgress ? 'Beräknar...' : 'Beräkna hash för alla bilder'}
                </Button>
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                <strong>Uppdatera dublett-lista:</strong> Snabb - bygger om listan baserat på befintliga hash-värden.<br/>
                <strong>Beräkna hash för alla bilder:</strong> Långsam (körs i bakgrunden) - beräknar nya fingeravtryck för alla bilder. Kör endast om nya bilder inte visas.
              </Typography>
            </Box>

            {/* Manuell sökning - Dold som standard */}
            <Box sx={{ borderTop: '1px solid #e0e0e0', pt: 2 }}>
              <Typography variant="body2" fontWeight="bold" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <SearchIcon fontSize="small" /> Manuell sökning per Asset ID
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField 
                  label="Asset ID" 
                  value={assetId} 
                  onChange={(e) => setAssetId(e.target.value)} 
                  size="small" 
                  fullWidth
                  placeholder="Klistra in Asset ID här"
                />
                <Button variant="contained" onClick={runSearch} sx={{ minWidth: 100 }} startIcon={<SearchIcon />}>Sök</Button>
              </Stack>
            </Box>

            {/* Liknande bilder (perceptual hash) - Avancerat */}
            <Box sx={{ borderTop: '1px solid #e0e0e0', pt: 2 }}>
              <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
                Sök "liknande" bilder (experimentellt)
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Denna funktion använder perceptuell hash för att hitta bilder som ser likadana ut men inte är exakt samma fil. Kräver ett Asset ID ovan.
              </Typography>
              <TextField 
                helperText="Tröskel 0-64: 0 = endast identiska, 8 = standard, högre = fler resultat men lägre precision."
                label="Tröskel för liknande bilder" 
                type="number" 
                value={threshold} 
                onChange={(e) => setThreshold(parseInt(e.target.value || '8', 10))} 
                size="small" 
                sx={{ width: { xs: '100%', sm: 300 } }}
                inputProps={{ min: 0, max: 64 }}
              />
            </Box>
          </Stack>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Paper>
      </Collapse>
      
      {/* Snackbar for notifications */}
      <Snackbar 
        open={snack.open} 
        autoHideDuration={5000} 
        onClose={() => setSnack(s => ({...s, open: false}))} 
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} sx={{ width: '100%' }}>{snack.message}</Alert>
      </Snackbar>

      <Box sx={{ mt: 2, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="h6">Exakta dubletter</Typography>
        {clusters.length > 0 && (
          <Chip 
            label={`${clusters.length} grupp${clusters.length !== 1 ? 'er' : ''}`} 
            size="small" 
            color="warning"
          />
        )}
      </Box>
      
      {loading ? (
        <Box sx={{ py: 4 }}>
          <LinearProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
            Laddar dubletter...
          </Typography>
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      ) : clusters.length === 0 ? (
        <Alert severity="success" sx={{ mt: 1 }}>
          <Typography variant="body2">
            <strong>Inga exakta dubletter hittade!</strong><br/>
            Om du nyligen laddat upp bilder och misstänker att det finns dubletter, klicka på "Avancerade verktyg" och sedan "Uppdatera dublett-lista".
          </Typography>
        </Alert>
      ) : (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Välj två bilder (A och B) i varje grupp för att jämföra eller slå ihop dem.
          </Typography>
          {clusters.map((c) => (
            <DuplicateClusterCard key={c._id} cluster={c} onRefresh={() => loadClusters(false)} />
          ))}
        </>
      )}

      {/* Scheduled duplicate check */}
      <Paper sx={{ p: 3, mt: 4 }}>
        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <ScheduleIcon /> Automatisk dublettkontroll
        </Typography>
        {scheduleLoading ? (
          <CircularProgress size={24} />
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={scheduleSettings.enabled}
                  onChange={(e) => setScheduleSettings(prev => ({ ...prev, enabled: e.target.checked }))}
                />
              }
              label="Aktivera automatisk nattlig dublettkontroll"
            />
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <TextField
                type="time"
                label="Tid (Stockholm)"
                value={scheduleSettings.dailyTime}
                onChange={(e) => setScheduleSettings(prev => ({ ...prev, dailyTime: e.target.value }))}
                size="small"
                sx={{ width: 160 }}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                type="number"
                label="Loggar behålls (dagar)"
                value={scheduleSettings.retentionDays}
                onChange={(e) => setScheduleSettings(prev => ({ ...prev, retentionDays: Math.max(1, Number(e.target.value) || 30) }))}
                size="small"
                sx={{ width: 180 }}
                inputProps={{ min: 1 }}
              />
              <Button
                variant="contained"
                onClick={handleSaveSchedule}
                disabled={scheduleSaving}
                sx={{ backgroundColor: '#072f53', '&:hover': { backgroundColor: '#0a3d6b' } }}
              >
                {scheduleSaving ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Spara'}
              </Button>
            </Box>
            {scheduleMsg && (
              <Alert severity={scheduleMsg.type} sx={{ mt: 1 }} onClose={() => setScheduleMsg(null)}>
                {scheduleMsg.text}
              </Alert>
            )}
            <Typography variant="body2" color="text.secondary">
              Kör automatiskt en fullständig ombyggnad av dublettlistan varje dygn vid angiven tid.
              Nya bilder hashas vid uppladdning, men listan byggs om för att fånga alla ändringar.
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Schedule logs */}
      <Paper sx={{ p: 3, mt: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Körningsloggar</Typography>
          <Button size="small" onClick={loadScheduleLogs} disabled={scheduleLogsLoading}>Uppdatera</Button>
        </Box>
        {scheduleLogsLoading ? (
          <CircularProgress size={24} />
        ) : scheduleLogs.length === 0 ? (
          <Typography variant="body2" color="text.secondary">Inga loggar tillgängliga ännu.</Typography>
        ) : (
          <Box>
            {scheduleLogs.map((summary: any) => (
              <Accordion key={summary.date} onChange={(_, expanded) => { if (expanded) handleViewScheduleLog(summary.date); }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <Typography sx={{ fontWeight: 500, minWidth: 100 }}>{summary.date}</Typography>
                    <Chip size="small" label={`${summary.runCount} körning${summary.runCount !== 1 ? 'ar' : ''}`} variant="outlined" />
                    <Chip size="small" label={`${summary.totalClusters} kluster`} color="info" variant="outlined" />
                    {summary.lastRunAt && (
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                        Senast: {new Date(summary.lastRunAt).toLocaleString('sv-SE')}
                      </Typography>
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {selectedScheduleLog && selectedScheduleLog.date === summary.date ? (
                    <Box>
                      {selectedScheduleLog.runs.map((run: any, idx: number) => (
                        <Paper key={run.runId} variant="outlined" sx={{ p: 2, mb: idx < selectedScheduleLog.runs.length - 1 ? 2 : 0 }}>
                          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                            <Chip
                              size="small"
                              label={run.trigger === 'scheduled' ? 'Schemalagd' : 'Manuell'}
                              variant="outlined"
                              color={run.trigger === 'scheduled' ? 'info' : 'default'}
                            />
                            <Typography variant="body2">
                              {new Date(run.startedAt).toLocaleTimeString('sv-SE')} — {new Date(run.finishedAt).toLocaleTimeString('sv-SE')}
                            </Typography>
                            <Chip size="small" label={`${run.clustersFound} kluster`} color="info" variant="outlined" />
                            <Chip size="small" label={`${run.assetsIndexed} hashade bilder`} variant="outlined" />
                            {run.error && <Chip size="small" label={run.error} color="error" variant="outlined" />}
                          </Box>
                        </Paper>
                      ))}
                    </Box>
                  ) : (
                    <CircularProgress size={20} />
                  )}
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default DuplicateListPage;


